import React, { useRef, useState, useEffect } from 'react';
import { Pose } from '@mediapipe/pose';
import { Camera } from '@mediapipe/camera_utils';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import { POSE_CONNECTIONS } from '@mediapipe/pose';
import { Card, Button } from '../components/common';

/**
 * MediaPipe Pose Detection Page
 * Free, open-source pose detection with rep counting
 */
const MediaPipePage = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseRef = useRef(null);
  const cameraRef = useRef(null);
  
  const [isTracking, setIsTracking] = useState(false);
  const [metrics, setMetrics] = useState({
    reps: 0,
    currentAngle: 0,
    hipHeight: 0,
    shoulderAngle: 0,
    kneeAngle: 0,
    elbowAngle: 0,
    status: 'Ready'
  });
  const [selectedExercise, setSelectedExercise] = useState('squat');
  const repStateRef = useRef('up'); // Use ref instead of state for immediate updates
  const lastHipYRef = useRef(null);
  const lastKneeAngleRef = useRef(null);

  const exercises = [
    { id: 'squat', name: 'Squats', icon: '🏋️', description: 'Knee bend detection' },
    { id: 'pushup', name: 'Push-ups', icon: '💪', description: 'Elbow bend detection' },
    { id: 'jumping_jack', name: 'Jumping Jacks', icon: '🤸', description: 'Arm raise detection' },
  ];

  useEffect(() => {
    // Don't initialize on mount, wait for user to click start
    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
    };
  }, []);

  const initializeMediaPipe = async () => {
    console.log('Initializing MediaPipe...');
    
    // Initialize MediaPipe Pose
    const pose = new Pose({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
      }
    });

    pose.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      enableSegmentation: false,
      smoothSegmentation: false,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      upperBodyOnly: false // We need full body for squats
    });

    pose.onResults(onResults);
    
    // Wait for pose to initialize
    await pose.initialize();
    poseRef.current = pose;
    console.log('MediaPipe initialized');

    // Initialize camera
    if (videoRef.current) {
      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          if (poseRef.current) {
            await poseRef.current.send({ image: videoRef.current });
          }
        },
        width: 640,
        height: 480
      });
      cameraRef.current = camera;
      console.log('Camera initialized');
    }
  };

  const onResults = (results) => {
    if (!canvasRef.current) return;
    if (!results.poseLandmarks) return;

    const canvasCtx = canvasRef.current.getContext('2d');
    const canvas = canvasRef.current;

    // Clear canvas
    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the pose - only body landmarks (skip face)
    canvasCtx.globalCompositeOperation = 'source-over';
    
    // Filter out face landmarks (0-10 are face/head)
    const bodyLandmarks = results.poseLandmarks.map((landmark, index) => {
      if (index <= 10) {
        // Return invisible landmark for face points
        return { ...landmark, visibility: 0 };
      }
      return landmark;
    });
    
    drawConnectors(canvasCtx, bodyLandmarks, POSE_CONNECTIONS, {
      color: '#00FF00',
      lineWidth: 4
    });
    drawLandmarks(canvasCtx, bodyLandmarks.filter((_, i) => i > 10), {
      color: '#FF0000',
      lineWidth: 2,
      radius: 6
    });

    canvasCtx.restore();

    // Count reps based on exercise - always run when we have landmarks
    if (selectedExercise === 'squat') {
      countSquatReps(results.poseLandmarks);
    } else if (selectedExercise === 'pushup') {
      countPushupReps(results.poseLandmarks);
    } else if (selectedExercise === 'jumping_jack') {
      countJumpingJackReps(results.poseLandmarks);
    }
  };

  const calculateAngle = (a, b, c) => {
    // Calculate angle between three points
    const radians = Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(a.y - b.y, a.x - b.x);
    let angle = Math.abs(radians * 180.0 / Math.PI);
    if (angle > 180.0) {
      angle = 360 - angle;
    }
    return angle;
  };

  const countSquatReps = (landmarks) => {
    // Get hip, knee, and ankle landmarks
    const leftHip = landmarks[23];
    const leftKnee = landmarks[25];
    const leftAnkle = landmarks[27];
    const rightHip = landmarks[24];
    const rightKnee = landmarks[26];
    const rightAnkle = landmarks[28];

    if (!leftHip || !leftKnee || !leftAnkle) {
      console.log('Missing landmarks:', { leftHip, leftKnee, leftAnkle });
      return;
    }

    // Calculate knee angle
    const leftKneeAngle = calculateAngle(leftHip, leftKnee, leftAnkle);
    const rightKneeAngle = rightKnee && rightAnkle ? calculateAngle(rightHip, rightKnee, rightAnkle) : leftKneeAngle;
    const kneeAngle = (leftKneeAngle + rightKneeAngle) / 2;
    
    // Calculate hip height (lower y = higher position)
    const hipHeight = Math.round((1 - leftHip.y) * 100);
    
    const roundedKneeAngle = Math.round(kneeAngle);
    console.log('Squat metrics:', { kneeAngle: roundedKneeAngle, hipHeight });
    
    setMetrics(prev => ({ 
      ...prev, 
      currentAngle: roundedKneeAngle,
      kneeAngle: roundedKneeAngle,
      hipHeight: hipHeight
    }));

    // Squat detection: knee angle < 120 = down, > 160 = up
    if (kneeAngle < 120 && repStateRef.current === 'up') {
      console.log('Squat DOWN detected, knee angle:', kneeAngle);
      repStateRef.current = 'down';
      setMetrics(prev => ({ ...prev, status: 'Going down... 🔽' }));
    } else if (kneeAngle > 160 && repStateRef.current === 'down') {
      console.log('Squat UP detected, knee angle:', kneeAngle, '- COUNTING REP!');
      repStateRef.current = 'up';
      setMetrics(prev => ({ 
        ...prev, 
        reps: prev.reps + 1,
        status: 'Rep completed! ✅'
      }));
      setTimeout(() => {
        setMetrics(prev => ({ ...prev, status: 'Ready for next rep' }));
      }, 1000);
    }
  };

  const countPushupReps = (landmarks) => {
    // Get shoulder, elbow, and wrist landmarks
    const leftShoulder = landmarks[11];
    const leftElbow = landmarks[13];
    const leftWrist = landmarks[15];

    if (!leftShoulder || !leftElbow || !leftWrist) return;

    // Calculate elbow angle
    const elbowAngle = calculateAngle(leftShoulder, leftElbow, leftWrist);
    
    setMetrics(prev => ({ ...prev, currentAngle: Math.round(elbowAngle) }));

    // Pushup detection: elbow angle < 90 = down, > 160 = up
    if (elbowAngle < 90 && repStateRef.current === 'up') {
      repStateRef.current = 'down';
      setMetrics(prev => ({ ...prev, status: 'Going down...' }));
    } else if (elbowAngle > 160 && repStateRef.current === 'down') {
      repStateRef.current = 'up';
      setMetrics(prev => ({ 
        ...prev, 
        reps: prev.reps + 1,
        status: 'Rep completed! ✅'
      }));
    }
  };

  const countJumpingJackReps = (landmarks) => {
    // Get shoulder and wrist landmarks
    const leftShoulder = landmarks[11];
    const leftWrist = landmarks[15];

    if (!leftShoulder || !leftWrist) return;

    // Check if arms are raised (wrist above shoulder)
    const armsRaised = leftWrist.y < leftShoulder.y;

    if (armsRaised && repStateRef.current === 'down') {
      repStateRef.current = 'up';
      setMetrics(prev => ({ 
        ...prev, 
        reps: prev.reps + 1,
        status: 'Jump! ✅'
      }));
    } else if (!armsRaised && repStateRef.current === 'up') {
      repStateRef.current = 'down';
    }
  };

  const handleStartTracking = async () => {
    setIsTracking(true);
    setMetrics({ reps: 0, currentAngle: 0, hipHeight: 0, shoulderAngle: 0, kneeAngle: 0, elbowAngle: 0, status: 'Starting camera...' });
    repStateRef.current = 'up';
    
    try {
      // Initialize MediaPipe first if not already done
      if (!poseRef.current) {
        await initializeMediaPipe();
      }
      
      // Start camera
      if (cameraRef.current) {
        await cameraRef.current.start();
        setMetrics(prev => ({ ...prev, status: 'Ready! Start exercising' }));
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setMetrics(prev => ({ ...prev, status: 'Camera error - check permissions' }));
      setIsTracking(false);
    }
  };

  const handleStopTracking = () => {
    setIsTracking(false);
    setMetrics(prev => ({ ...prev, status: 'Stopped' }));
    
    if (cameraRef.current) {
      cameraRef.current.stop();
    }
  };

  const handleExerciseChange = (exerciseId) => {
    setSelectedExercise(exerciseId);
    setMetrics({ reps: 0, currentAngle: 0, hipHeight: 0, shoulderAngle: 0, kneeAngle: 0, elbowAngle: 0, status: 'Ready' });
    repStateRef.current = 'up';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-slate-100 mb-2">
            🎯 MediaPipe Pose Detection
          </h1>
          <p className="text-slate-400">
            Free, open-source pose tracking with automatic rep counting
          </p>
        </div>

        {/* Exercise Selection */}
        <Card className="mb-6">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Select Exercise</h3>
          <div className="flex gap-3 flex-wrap">
            {exercises.map(ex => (
              <button
                key={ex.id}
                onClick={() => handleExerciseChange(ex.id)}
                disabled={isTracking}
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  selectedExercise === ex.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                } ${isTracking ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="text-2xl mb-1">{ex.icon}</div>
                <div className="text-sm font-semibold">{ex.name}</div>
                <div className="text-xs opacity-75">{ex.description}</div>
              </button>
            ))}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Camera */}
          <div className="lg:col-span-2 space-y-6">
            <Card padding="none">
              <div className="relative bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <video
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1]"
                  style={{ display: isTracking ? 'block' : 'none' }}
                  autoPlay
                  playsInline
                  muted
                />
                <canvas
                  ref={canvasRef}
                  width={640}
                  height={480}
                  className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1]"
                />
                
                {!isTracking && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-4">📹</div>
                      <p className="text-slate-400">Click "Start Tracking" to begin</p>
                    </div>
                  </div>
                )}
                
                <div className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-3">
                  <p className="text-slate-100 font-semibold">
                    {exercises.find(e => e.id === selectedExercise)?.name}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {isTracking ? '🟢 Tracking Active' : '🔴 Stopped'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Controls */}
            <Card>
              <div className="flex gap-3 justify-center">
                {!isTracking ? (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleStartTracking}
                  >
                    Start Tracking
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={handleStopTracking}
                  >
                    Stop Tracking
                  </Button>
                )}
              </div>
              <p className="text-center text-slate-500 text-sm mt-3">
                MediaPipe detects 33 body landmarks in real-time
              </p>
            </Card>
          </div>

          {/* Right - Metrics */}
          <div className="space-y-4">
            {/* Reps Counter */}
            <Card className="bg-gradient-to-br from-primary-600 to-primary-700 border-primary-500">
              <div className="text-center">
                <p className="text-primary-100 text-sm mb-2">Reps Completed</p>
                <div className="text-7xl font-bold text-white mb-2 animate-pulse">
                  {metrics.reps}
                </div>
                <p className="text-primary-100 text-sm">
                  {exercises.find(e => e.id === selectedExercise)?.name}
                </p>
              </div>
            </Card>

            {/* Knee Angle */}
            {selectedExercise === 'squat' && isTracking && (
              <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500">
                <div className="text-center">
                  <p className="text-blue-100 text-sm mb-2">Knee Angle</p>
                  <div className="text-5xl font-bold text-white mb-2">
                    {metrics.kneeAngle || 0}°
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-blue-900 rounded-full h-2">
                      <div 
                        className="bg-blue-300 h-2 rounded-full transition-all duration-200"
                        style={{ width: `${Math.min(((metrics.kneeAngle || 0) / 180) * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-blue-200 text-xs mt-1">
                      {metrics.kneeAngle < 120 ? 'Deep squat 🔽' : metrics.kneeAngle > 160 ? 'Standing 🔼' : metrics.kneeAngle > 0 ? 'Mid range' : 'Detecting...'}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Hip Height */}
            {selectedExercise === 'squat' && isTracking && (
              <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-purple-500">
                <div className="text-center">
                  <p className="text-purple-100 text-sm mb-2">Hip Height</p>
                  <div className="text-4xl font-bold text-white mb-2">
                    {metrics.hipHeight || 0}%
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-purple-900 rounded-full h-2">
                      <div 
                        className="bg-purple-300 h-2 rounded-full transition-all duration-200"
                        style={{ width: `${metrics.hipHeight || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Status */}
            <Card className="bg-gradient-to-br from-yellow-600 to-yellow-700 border-yellow-500">
              <div className="text-center">
                <p className="text-yellow-100 text-sm mb-2">Status</p>
                <div className="text-lg font-bold text-white">
                  {metrics.status}
                </div>
              </div>
            </Card>

            {/* Info */}
            <Card title="ℹ️ How It Works">
              <div className="space-y-2 text-sm text-slate-400">
                <p><strong className="text-slate-200">Technology:</strong> MediaPipe Pose</p>
                <p><strong className="text-slate-200">Landmarks:</strong> 33 body points</p>
                <p><strong className="text-slate-200">Cost:</strong> 100% Free!</p>
                <p><strong className="text-slate-200">Privacy:</strong> Runs locally</p>
              </div>
            </Card>

            {/* Instructions */}
            <Card title="📋 Instructions">
              <div className="space-y-2 text-sm text-slate-400">
                <p>1. Select an exercise</p>
                <p>2. Click "Start Tracking"</p>
                <p>3. Allow camera access</p>
                <p>4. Position yourself in frame</p>
                <p>5. Start exercising!</p>
                <p className="text-primary-400 mt-3">
                  💡 Reps count automatically based on body angles
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPipePage;
